/**
 * useUploadStationImage - Mutation hook for uploading station images
 * Handles the three-step upload process with optimistic UI:
 * 1. Get signed URL
 * 2. Upload to GCS
 * 3. Confirm upload (triggers AI moderation)
 */

import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getStationImageUploadUrl,
  uploadToSignedUrl,
  confirmStationImageUpload,
  isModerationError,
} from '../../api/stationImages';
import {
  StationImageType,
  IUploadingImage,
  IStationImagesResponse,
  IStationMediaImage,
  IMAGE_TYPE_CONFIGS,
  ImageValidationResult,
} from '../../components/StationMedia/types';
import { STATION_IMAGES_QUERY_KEY } from './useStationImages';
import { useAdminStore } from '../../store/adminStore';

interface UploadResult {
  success: boolean;
  image?: IStationMediaImage;
  error?: string;
  status?: 'confirmed' | 'pending_review';
}

interface UseUploadStationImageReturn {
  uploadImage: (file: File, type: StationImageType) => Promise<UploadResult>;
  uploadMultiple: (files: File[], type: StationImageType) => Promise<UploadResult[]>;
  uploadingImages: IUploadingImage[];
  clearCompleted: () => void;
  clearError: (tempId: string) => void;
  isUploading: boolean;
}

/**
 * Validates a file against the image type configuration
 */
const validateFile = (
  file: File,
  type: StationImageType
): ImageValidationResult => {
  const config = IMAGE_TYPE_CONFIGS[type];

  // Check file type
  if (!config.acceptTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `Invalid file type. Accepted: ${config.acceptTypes.join(', ')}`,
    };
  }

  // Check file size
  if (file.size > config.maxSize) {
    const maxSizeMB = config.maxSize / (1024 * 1024);
    return {
      isValid: false,
      error: `File too large. Maximum size: ${maxSizeMB}MB`,
    };
  }

  return { isValid: true };
};

/**
 * Generates a unique temporary ID for uploading images
 */
const generateTempId = (): string => {
  return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Hook for uploading station images with progress tracking and optimistic UI
 */
const useUploadStationImage = (stationId: string): UseUploadStationImageReturn => {
  const queryClient = useQueryClient();
  const adminId = useAdminStore((state) => state.admin.admin?.id);
  const [uploadingImages, setUploadingImages] = useState<IUploadingImage[]>([]);
  const objectUrlsRef = useRef<Set<string>>(new Set());

  /**
   * Updates the progress/status of an uploading image
   */
  const updateUploadingImage = useCallback(
    (
      tempId: string,
      updates: Partial<IUploadingImage>
    ) => {
      setUploadingImages((prev) =>
        prev.map((img) =>
          img.id === tempId ? { ...img, ...updates } : img
        )
      );
    },
    []
  );

  /**
   * Adds an optimistic image to the React Query cache
   */
  const addOptimisticImage = useCallback(
    (type: StationImageType, previewUrl: string, tempId: string) => {
      queryClient.setQueryData<IStationImagesResponse>(
        [STATION_IMAGES_QUERY_KEY, stationId],
        (old) => {
          if (!old) {
            return {
              icon: null,
              banner: null,
              gallery: [],
            };
          }

          const optimisticImage: IStationMediaImage = {
            id: tempId,
            url: previewUrl,
            content_type: 'image/jpeg',
            size: 0,
            status: 'pending',
            created_at: new Date().toISOString(),
          };

          if (type === 'icon') {
            return { ...old, icon: optimisticImage };
          } else if (type === 'banner') {
            return { ...old, banner: optimisticImage };
          } else {
            return {
              ...old,
              gallery: [...old.gallery, optimisticImage],
            };
          }
        }
      );
    },
    [queryClient, stationId]
  );

  /**
   * Removes an optimistic image from the cache (on error/rollback)
   */
  const removeOptimisticImage = useCallback(
    (type: StationImageType, tempId: string) => {
      queryClient.setQueryData<IStationImagesResponse>(
        [STATION_IMAGES_QUERY_KEY, stationId],
        (old) => {
          if (!old) return old;

          if (type === 'icon' && old.icon?.id === tempId) {
            return { ...old, icon: null };
          } else if (type === 'banner' && old.banner?.id === tempId) {
            return { ...old, banner: null };
          } else if (type === 'gallery') {
            return {
              ...old,
              gallery: old.gallery.filter((img) => img.id !== tempId),
            };
          }
          return old;
        }
      );
    },
    [queryClient, stationId]
  );

  /**
   * Uploads a single image through the three-step process
   */
  const uploadImage = useCallback(
    async (file: File, type: StationImageType): Promise<UploadResult> => {
      // Validate file
      const validation = validateFile(file, type);
      if (!validation.isValid) {
        return { success: false, error: validation.error };
      }

      const tempId = generateTempId();
      const previewUrl = URL.createObjectURL(file);
      objectUrlsRef.current.add(previewUrl);

      // Create uploading image state
      const uploadingImage: IUploadingImage = {
        id: tempId,
        file,
        type,
        progress: 0,
        status: 'pending',
        previewUrl,
      };

      // Add to uploading state, clearing any previous error/completed uploads of same type
      // This ensures the new upload is shown instead of old error states
      setUploadingImages((prev) => {
        // For icon/banner, clear all previous uploads of same type (only 1 allowed)
        // For gallery, only clear errors to allow multiple concurrent uploads
        const filtered = prev.filter((img) => {
          if (img.type !== type) return true;
          if (type === 'gallery') {
            // For gallery, only remove error states
            return img.status !== 'error';
          }
          // For icon/banner, remove any existing upload of same type
          // (cleanup Object URL if removing)
          if (objectUrlsRef.current.has(img.previewUrl)) {
            URL.revokeObjectURL(img.previewUrl);
            objectUrlsRef.current.delete(img.previewUrl);
          }
          return false;
        });
        return [...filtered, uploadingImage];
      });

      // Add optimistic image to cache
      addOptimisticImage(type, previewUrl, tempId);

      try {
        // Step 1: Get signed URL (10% progress)
        updateUploadingImage(tempId, { status: 'uploading', progress: 5 });

        const uploadUrlResponse = await getStationImageUploadUrl(
          stationId,
          type,
          file.type,
          file.name,
          adminId || undefined
        );

        updateUploadingImage(tempId, {
          progress: 10,
          serverImageId: uploadUrlResponse.image_id,
        });

        // Step 2: Upload to GCS (10-90% progress)
        await uploadToSignedUrl(
          uploadUrlResponse.upload_url,
          file,
          uploadUrlResponse.content_type,
          (progress) => {
            const scaledProgress = 10 + Math.round(progress * 0.8);
            updateUploadingImage(tempId, { progress: scaledProgress });
          }
        );

        // Step 3: Confirm upload (90-100% progress)
        updateUploadingImage(tempId, { status: 'confirming', progress: 92 });

        const confirmedResponse = await confirmStationImageUpload(
          stationId,
          uploadUrlResponse.image_id
        );

        // Create the confirmed image object
        const confirmedImage: IStationMediaImage = {
          id: confirmedResponse.image_id,
          url: confirmedResponse.url,
          content_type: file.type,
          size: file.size,
          status: confirmedResponse.status,
          created_at: new Date().toISOString(),
        };

        // Mark upload as completed
        updateUploadingImage(tempId, { status: 'completed', progress: 100 });

        // Invalidate the query to refetch fresh data from server
        // This ensures the UI is always in sync with the backend
        await queryClient.invalidateQueries({
          queryKey: [STATION_IMAGES_QUERY_KEY, stationId],
        });

        // Cleanup after a short delay to show completion
        setTimeout(() => {
          setUploadingImages((prev) => prev.filter((img) => img.id !== tempId));
          if (objectUrlsRef.current.has(previewUrl)) {
            URL.revokeObjectURL(previewUrl);
            objectUrlsRef.current.delete(previewUrl);
          }
        }, 500);

        return { success: true, image: confirmedImage, status: confirmedResponse.status };
      } catch (error) {
        // Rollback optimistic update
        removeOptimisticImage(type, tempId);

        const errorMessage = isModerationError(error)
          ? `Image rejected: ${error.reason}`
          : error instanceof Error
          ? error.message
          : 'Upload failed';

        updateUploadingImage(tempId, {
          status: 'error',
          error: errorMessage,
          progress: 0,
        });

        // Invalidate cache to ensure fresh state for next upload attempt
        await queryClient.invalidateQueries({
          queryKey: [STATION_IMAGES_QUERY_KEY, stationId],
        });

        return { success: false, error: errorMessage };
      }
    },
    [
      stationId,
      adminId,
      queryClient,
      addOptimisticImage,
      removeOptimisticImage,
      updateUploadingImage,
    ]
  );

  /**
   * Uploads multiple files (for gallery bulk upload)
   */
  const uploadMultiple = useCallback(
    async (files: File[], type: StationImageType): Promise<UploadResult[]> => {
      const results = await Promise.all(
        files.map((file) => uploadImage(file, type))
      );
      return results;
    },
    [uploadImage]
  );

  /**
   * Clears completed uploads from the uploading state
   */
  const clearCompleted = useCallback(() => {
    setUploadingImages((prev) => {
      const completed = prev.filter((img) => img.status === 'completed');
      completed.forEach((img) => {
        if (objectUrlsRef.current.has(img.previewUrl)) {
          URL.revokeObjectURL(img.previewUrl);
          objectUrlsRef.current.delete(img.previewUrl);
        }
      });
      return prev.filter((img) => img.status !== 'completed');
    });
  }, []);

  /**
   * Clears an error and removes the failed upload
   */
  const clearError = useCallback((tempId: string) => {
    setUploadingImages((prev) => {
      const toRemove = prev.find((img) => img.id === tempId);
      if (toRemove && objectUrlsRef.current.has(toRemove.previewUrl)) {
        URL.revokeObjectURL(toRemove.previewUrl);
        objectUrlsRef.current.delete(toRemove.previewUrl);
      }
      return prev.filter((img) => img.id !== tempId);
    });
  }, []);

  const isUploading = uploadingImages.some(
    (img) => img.status === 'uploading' || img.status === 'confirming'
  );

  return {
    uploadImage,
    uploadMultiple,
    uploadingImages,
    clearCompleted,
    clearError,
    isUploading,
  };
};

export default useUploadStationImage;
